import { useDashboardFilters } from "@/dashboard/store/useDashboardFilters";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LocationSelect } from "../location-select";
import { Space } from "./types";

interface SpaceInputProps {
  location: string;
  data: Space[];
  isLoading: boolean;
  selectedCity: { id: string; name: string }[] | null;
  onSubmit?: boolean;
  onChange?: (value: Space[] | null) => void;
}

/**
 * Espaços da modal "Mais filtros" do Painel. As cidades escolhidas limitam os
 * espaços listados; com `onSubmit`, a seleção só vai ao store em "Filtrar".
 */
export function SpaceInput({
  location,
  data,
  isLoading,
  selectedCity,
  onSubmit,
  onChange,
}: SpaceInputProps) {
  const { t } = useTranslation(["dashboard"]);
  const { filters, setFilters } = useDashboardFilters();

  const handleChange = useCallback(
    (value: Space[] | null) => {
      if (onSubmit) {
        onChange?.(value);
      } else {
        setFilters({ spaces: value as Space[] });
      }
    },
    [onChange, onSubmit, setFilters],
  );

  const options = useMemo(
    () =>
      selectedCity && selectedCity.length > 0
        ? data.filter((space) =>
            selectedCity.some((city) => city.id === space.city),
          )
        : data,
    [selectedCity, data],
  );

  return (
    <LocationSelect<Space>
      id={`${location}-space-filter`}
      label={t("space-filter-label") as string}
      placeholder={t("space-filter-placeholder") as string}
      searchPlaceholder={t("space-filter-search-placeholder") as string}
      emptyText={t("space-filter-empty-state") as string}
      multipleLabel={(count) =>
        t("space-filter-selected-multiple", { count }) as string
      }
      options={options}
      value={filters.spaces}
      isLoading={isLoading}
      onChange={handleChange}
    />
  );
}
